<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import EcommerceAPI from 'dashboard/api/ecommerce';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  contactId: {
    type: [Number, String],
    required: true,
  },
  currentOrderId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['select', 'unlink']);

const { t } = useI18n();

const orders = ref([]);
const loading = ref(true);
const error = ref('');
const selectedId = ref(props.currentOrderId);

const fetchOrders = async () => {
  if (!props.contactId) return;
  try {
    loading.value = true;
    const response = await EcommerceAPI.getContactOrders(props.contactId);
    orders.value = response.data.orders;
  } catch (e) {
    error.value = e.response?.data?.error || 'Error loading orders';
  } finally {
    loading.value = false;
  }
};

watch(() => props.contactId, fetchOrders, { immediate: true });

watch(
  () => props.currentOrderId,
  id => {
    selectedId.value = id;
  }
);

const formatCurrency = (amount, currency) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: currency || 'BRL',
  }).format(amount);
};

const orderOptions = computed(() =>
  orders.value.map(order => ({
    value: order.id,
    label: `#${order.order_number} — ${formatCurrency(order.total_amount, order.currency)}`,
  }))
);

const handleSelect = value => {
  if (!value) {
    emit('unlink');
    return;
  }
  const order = orders.value.find(o => o.id === value);
  if (order) emit('select', order);
};

const handleUnlink = () => {
  selectedId.value = null;
  emit('unlink');
};
</script>

<template>
  <div class="flex flex-col gap-1.5">
    <ComboBox
      :model-value="selectedId"
      :options="orderOptions"
      :placeholder="t('KANBAN.ECOMMERCE_ORDER.SELECT')"
      :search-placeholder="t('KANBAN.ECOMMERCE_ORDER.SELECT')"
      :empty-state="loading ? '...' : t('KANBAN.ECOMMERCE_ORDER.NO_ORDERS')"
      :disabled="loading || !!error"
      @update:model-value="handleSelect"
    />
    <div v-if="error" class="text-xs text-n-ruby-11">
      {{ error }}
    </div>
    <Button
      v-if="currentOrderId"
      variant="ghost"
      size="xs"
      :label="t('KANBAN.ECOMMERCE_ORDER.UNLINK')"
      icon="i-lucide-unlink"
      class="!justify-start !w-fit"
      @click="handleUnlink"
    />
  </div>
</template>
